import React, { useState } from 'react';

export default function BulkSender() {
  const [file, setFile] = useState(null);
  const [headerImage, setHeaderImage] = useState(null); 
  const [status, setStatus] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isDragging, setIsDragging] = useState(false);

  const [totalRecords, setTotalRecords] = useState(0);
  const [sentRecords, setSentRecords] = useState(0);
  const [isSending, setIsSending] = useState(false);

  const [isCompleted, setIsCompleted] = useState(false);

  const [backendPreviews, setBackendPreviews] = useState([]);
  const [activeMessage, setActiveMessage] = useState(null);

  const [isDarkTheme, setIsDarkTheme] = useState(true);

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      setFile(e.target.files[0]); 
    }
  };

  const handleImageChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      setHeaderImage(e.target.files[0]);
      setStatus(''); 
    }
  };

  const handleDragOver = (e) => { e.preventDefault(); setIsDragging(true); };
  const handleDragLeave = () => { setIsDragging(false); };
  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      setFile(e.dataTransfer.files[0]);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // --- UPDATED VALIDATION ---
    if (!headerImage) {
      setStatus('❌ Slip Header Image is compulsory! Please upload an image.');
      return;
    }
    if (!file) {
      setStatus('❌ Please select a Valid Voter List (Excel) file.');
      return;
    }

    setIsLoading(true);
    setStatus('Initializing Server Connection...');

    const formData = new FormData();
    formData.append('file', file);
    
    // --- HARDCODED TEMPLATE & LANGUAGE ---
    formData.append('templateName', 'reminder2');
    formData.append('languageCode', 'en');
    
    formData.append('headerImage', headerImage);

    try {
      const response = await fetch('https://bulkslip.bjpwings.com/api/broadcast', {
        method: 'POST',
        body: formData,
      });

      const data = await response.json();

      if (response.ok) {
        setBackendPreviews(data.previews);
        setTotalRecords(data.total_records);
        setActiveMessage(data.previews[0]);
        setIsSending(true);
        setIsCompleted(false);

        let current = 0;
        const interval = setInterval(() => {
          current += 1;
          setSentRecords(current);
          if (data.previews[current]) {
            setActiveMessage(data.previews[current]);
          }
          if (current >= data.total_records) {
            clearInterval(interval);
            setIsSending(false);
            setIsLoading(false);

            setIsCompleted(true);
            setStatus('');
          }
        }, 1250);

      } else {
        setStatus(`❌ Error: ${data.error}`);
        setIsLoading(false);
      }
    } catch (error) {
      setStatus(`❌ Network Error.`);
      setIsLoading(false);
    }
  };

  // --- DYNAMIC THEME VARIABLES ---
  const t = {
    bg: isDarkTheme ? '#111827' : '#F1F5F9',
    cardBg: isDarkTheme ? '#1F2937' : '#FFFFFF',
    cardBorder: isDarkTheme ? '#374151' : '#E2E8F0',
    shadow: isDarkTheme ? '0 25px 50px -12px rgba(0, 0, 0, 0.5)' : '0 10px 25px -5px rgba(0, 0, 0, 0.05)',
    heading: isDarkTheme ? '#ffffff' : '#1E3A8A',
    subheading: isDarkTheme ? '#9CA3AF' : '#64748B',
    label: isDarkTheme ? '#F0F9FF' : '#334155',
    accent: isDarkTheme ? '#6366F1' : '#60A5FA',
    bubbleBg: isDarkTheme ? '#1F2937' : '#E2F7CB',
    bubbleBorder: isDarkTheme ? '1px solid #374151' : 'none',
    bubbleText: isDarkTheme ? '#F0F9FF' : '#1E293B',
    bubbleMuted: isDarkTheme ? '#9CA3AF' : '#475569',
    bubbleImgBg: isDarkTheme ? '#111827' : '#CBD5E1',
    slogan: isDarkTheme ? '#E11D48' : '#1E3A8A',
    tick: isDarkTheme ? '#6366F1' : '#3B82F6',
    progressBg: isDarkTheme ? '#111827' : '#F1F5F9',
    boxBg: isDarkTheme ? '#111827' : '#F8FAFC',
    statusBg: isDarkTheme ? 'rgba(99, 102, 241, 0.1)' : '#F0FDF4',
    statusBorder: isDarkTheme ? '#6366F1' : '#BBF7D0',
    statusLabel: isDarkTheme ? '#6366F1' : '#16A34A',
    statusText: isDarkTheme ? '#F0F9FF' : '#15803D',
    statusSpinner: isDarkTheme ? '#6366F1' : '#16A34A',
    transmitText: isDarkTheme ? '#6366F1' : '#1E3A8A',
    totalText: isDarkTheme ? '#6366F1' : '#94A3B8',
    remainingText: isDarkTheme ? '#ffffff' : '#1E3A8A',
    successIconBg: isDarkTheme ? 'rgba(16, 185, 129, 0.1)' : '#F0FDF4',
    successIconColor: isDarkTheme ? '#10B981' : '#16A34A'
  };

  return (
    <div className="position-relative d-flex flex-column justify-content-center align-items-center min-vh-100 py-5 px-3 px-md-5" style={{ backgroundColor: t.bg, fontFamily: "'Inter', sans-serif", overflowX: 'hidden', transition: 'background-color 0.3s ease' }}>

      {/* THEME TOGGLE BUTTON */}
      <button
        onClick={() => setIsDarkTheme(!isDarkTheme)}
        className="btn shadow-sm"
        style={{
          position: 'absolute',
          top: '1.5rem',
          right: '1.5rem',
          backgroundColor: t.cardBg,
          color: t.heading,
          border: `1px solid ${t.cardBorder}`,
          borderRadius: '25px',
          fontWeight: 'bold',
          zIndex: 1000,
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          padding: '0.5rem 1rem',
          transition: 'all 0.3s ease'
        }}
      >
        {isDarkTheme ? 'Light' : 'Dark'}
      </button>

      {/* DYNAMIC CSS GENERATOR */}
      <style>{`
        :root {
          --bs-primary: ${isDarkTheme ? '#6366F1' : '#1E3A8A'}; 
        }
        
        .form-control::placeholder { color: ${isDarkTheme ? '#9CA3AF' : '#94A3B8'}; opacity: 0.8; }
        .dynamic-input {
          background-color: ${isDarkTheme ? '#111827' : '#FFFFFF'} !important; 
          border: 1px solid ${isDarkTheme ? '#374151' : '#CBD5E1'} !important; 
          color: ${isDarkTheme ? '#F0F9FF' : '#334155'} !important; 
          transition: all 0.3s ease;
        }
        .dynamic-input:focus {
          background-color: ${isDarkTheme ? '#1F2937' : '#FFFFFF'} !important; 
          border-color: ${isDarkTheme ? '#6366F1' : '#60A5FA'} !important; 
          box-shadow: 0 0 0 0.25rem ${isDarkTheme ? 'rgba(99, 102, 241, 0.25)' : 'rgba(96, 165, 250, 0.25)'} !important;
        }
        
        .border-dashed { border-style: dashed !important; border-width: 2px !important; }
        .drop-zone { background-color: ${isDarkTheme ? '#111827' : '#F8FAFC'}; border-color: ${isDarkTheme ? '#374151' : '#CBD5E1'}; }
        .drop-zone:hover { background-color: ${isDarkTheme ? '#1F2937' : '#F1F5F9'}; border-color: ${isDarkTheme ? '#6366F1' : '#60A5FA'}; }
        .drop-zone.dragging { background-color: ${isDarkTheme ? 'rgba(99, 102, 241, 0.1)' : 'rgba(96, 165, 250, 0.1)'}; border-color: ${isDarkTheme ? '#6366F1' : '#60A5FA'}; }

        .btn-primary { color: #FFFFFF !important; font-weight: 700 !important; border: none !important; transition: all 0.3s ease; }
        .btn-primary:hover { background-color: ${isDarkTheme ? '#4F46E5' : '#1E40AF'} !important; }

        @keyframes bubbleEntry {
          0% { opacity: 0; transform: translateY(20px); }
          100% { opacity: 1; transform: translateY(0); }
        }
        .bubble-entry-animate { animation: bubbleEntry 0.5s cubic-bezier(0.16, 1, 0.3, 1) forwards; }

        @keyframes contentSlideIn {
          0% { opacity: 0; transform: translateX(15px); }
          100% { opacity: 1; transform: translateX(0); }
        }
        .content-slide-animate { animation: contentSlideIn 0.4s ease-out forwards; }

        @keyframes imagePulse {
          0% { transform: scale(0.98); opacity: 0.9; }
          50% { transform: scale(1.01); opacity: 1; }
          100% { transform: scale(1); opacity: 1; }
        }
        .image-pulse-animate { animation: imagePulse 0.6s ease-out forwards; }

        @keyframes tickFade {
          0% { opacity: 0; transform: scale(0.5); }
          100% { opacity: 1; transform: scale(1); }
        }
        .tick-animate { animation: tickFade 0.3s ease-out forwards; }
      `}</style>

      {/* 1. SETUP AREA */}
      {!isSending && !isCompleted && (
        <div className="card border-0 rounded-4 p-4 p-md-5 w-100 bubble-entry-animate" style={{ maxWidth: '500px', backgroundColor: t.cardBg, border: `1px solid ${t.cardBorder} !important`, boxShadow: t.shadow, transition: 'all 0.3s ease' }}>
          <div className="text-center mb-4">
            <h2 className="fw-bolder mb-1" style={{ color: t.heading }}>Bulk Slip Whatsapp Send</h2>
            <p className="mb-0" style={{ color: t.subheading }}></p>
          </div>

          <form onSubmit={handleSubmit} className="d-flex flex-column gap-3">
            
            <div>
              <label className="form-label fw-bold mb-1" style={{ color: t.label }}>
                Slip Image (Applies to all slips) <span className="text-danger">*</span>
              </label>
              <input 
                type="file" 
                className={`form-control form-control-lg dynamic-input rounded-3 ${!headerImage && status.includes('Header') ? 'border-danger' : ''}`} 
                accept="image/*"
                onChange={handleImageChange} 
                required
              />
              <small style={{ color: t.subheading }}>Uploading a header image is compulsory.</small>
            </div>

            <div>
              <label className="form-label fw-bold mb-1" style={{ color: t.label }}>
                Voter List (Excel) <span className="text-danger">*</span>
              </label>
              <div
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                onClick={() => document.getElementById('fileInput').click()}
                className={`border-dashed rounded-4 p-4 p-md-5 text-center position-relative drop-zone ${isDragging ? 'dragging' : ''} ${!file && status.includes('Excel') ? 'border-danger bg-danger bg-opacity-10' : ''}`}
                style={{ cursor: 'pointer', transition: 'all 0.2s ease' }}
              >
                <div className="mb-3 d-flex justify-content-center">
                  <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke={file || isDragging ? t.accent : t.subheading} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{ transition: 'stroke 0.3s' }}>
                    <path d="M4 14.899A7 7 0 1 1 15.71 8h1.79a4.5 4.5 0 0 1 2.5 8.242"></path>
                    <path d="M12 12v9"></path>
                    <path d="M8 16l4-4 4 4"></path>
                  </svg>
                </div>

                <h6 className="fw-bold" style={{ color: file ? t.accent : t.label, wordBreak: 'break-word' }}>
                  {file ? file.name : "Click or drop Excel file here"}
                </h6>
                {!file && <small style={{ color: t.subheading }}>Supports .xlsx and .xls</small>}
                <input id="fileInput" type="file" className="d-none" accept=".xlsx, .xls" onChange={handleFileChange} />
              </div>
            </div>

            <button
              type="submit"
              disabled={isLoading}
              className="btn btn-primary btn-lg w-100 mt-3 py-3 fw-bold rounded-3 shadow-sm d-flex justify-content-center align-items-center gap-2"
              style={{ letterSpacing: '0.5px' }}
            >
              {isLoading ? (
                <>
                  <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                  Connecting to Server...
                </>
              ) : (
                'Start Bulk Broadcast'
              )}
            </button>
          </form>

          {status && (
            <div className={`alert mt-4 mb-0 text-center border-0 fw-semibold ${status.includes('❌') ? 'bg-danger bg-opacity-25 text-danger' : 'bg-success bg-opacity-25 text-success'}`}>
              {status}
            </div>
          )}
        </div>
      )}

      {/* 2. BROADCAST AREA WITH ANIMATION */}
      {isSending && !isCompleted && (
        <div className="w-100" style={{ maxWidth: '950px' }}>

          <div className="text-center mb-4 mb-md-5">
            <h2 className="fw-bolder mb-1" style={{ color: t.heading }}>Live Delivery Feed</h2>
            <p className="mb-0" style={{ color: t.subheading }}>Monitoring real-time transmissions securely</p>
          </div>

          <div className="row g-4 align-items-start">

            <div className="col-12 col-md-5 d-flex justify-content-center px-4 px-md-2 order-2 order-md-1">
              <div
                className="bubble-entry-animate position-relative p-3 p-md-4 rounded-4 w-100"
                style={{ maxWidth: '380px', backgroundColor: t.bubbleBg, color: t.bubbleText, border: t.bubbleBorder, borderTopLeftRadius: 0, boxShadow: '0 15px 35px rgba(0,0,0,0.1)', transition: 'all 0.3s ease' }}
              >
                <div key={`img-${sentRecords}`} className="image-pulse-animate rounded-3 mb-3 d-flex justify-content-center align-items-center overflow-hidden" style={{ width: '100%', backgroundColor: '#111827' }}>
                  <img
                    src={activeMessage?.image}
                    alt="Voter"
                    style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                  />
                </div>

                <div style={{ fontSize: '0.95rem', lineHeight: '1.4' }}>
                  <p className="mb-2" style={{ color: t.bubbleMuted }}>Hello Sir/Ma'am</p>

                  <div key={`text-${sentRecords}`} className="content-slide-animate">
                    <p className={`fw-bold mb-1 ${isDarkTheme ? 'text-white' : 'text-dark'}`}>{activeMessage?.var1}</p>
                    <p className={`fw-bold mb-1 ${isDarkTheme ? 'text-white' : 'text-dark'}`}>{activeMessage?.var2}</p>
                    <p className={`fw-bold mb-1 ${isDarkTheme ? 'text-white' : 'text-dark'}`}>{activeMessage?.var3}</p>
                    <p className={`fw-bold mb-3 ${isDarkTheme ? 'text-white' : 'text-dark'}`}>{activeMessage?.var4}</p>
                  </div>

                  <p className="mb-2" style={{ color: t.bubbleMuted }}>You can check the details below for your convenience.</p>
                  <p className="fw-bold mb-3" style={{ color: t.slogan }}>{activeMessage?.var5}</p>
                  <p className="small mb-0" style={{ color: t.bubbleMuted }}>🙂 Thanks for taking a moment to read this message.</p>
                </div>

                <div className="d-flex justify-content-end align-items-center mt-2 small gap-1" style={{ color: t.bubbleMuted }}>
                  <span>{new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                  <span key={`tick-${sentRecords}`} className="tick-animate fs-6" style={{ color: t.tick }}>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="18 6 11 15 7 11"></polyline>
                      <polyline points="22 6 15 15 13 13"></polyline>
                    </svg>
                  </span>
                </div>

                <div className="position-absolute top-0 start-0 translate-middle-x" style={{ width: 0, height: 0, borderTop: `16px solid ${t.bubbleBg}`, borderLeft: '16px solid transparent', transition: 'border-top-color 0.3s ease' }}></div>
              </div>
            </div>

            <div className="col-12 col-md-7 order-1 order-md-2">
              <div className="card border-0 rounded-4 p-4 p-md-5 h-100" style={{ backgroundColor: t.cardBg, border: `1px solid ${t.cardBorder} !important`, boxShadow: t.shadow, transition: 'all 0.3s ease' }}>

                <div className="mb-4 mb-md-5">
                  <div className="d-flex justify-content-between align-items-end mb-2">
                    <span className="fw-bold text-uppercase small tracking-wide" style={{ color: t.subheading }}>Broadcast Status</span>
                    <span className="fs-2 fw-bolder lh-1" style={{ color: t.accent }}>
                      {sentRecords} <span className="fs-6 fw-bold" style={{ color: t.totalText }}>/ {totalRecords}</span>
                    </span>
                  </div>
                  <div className="progress rounded-pill" style={{ height: '12px', backgroundColor: t.progressBg }}>
                    <div
                      className="progress-bar rounded-pill progress-bar-striped progress-bar-animated"
                      role="progressbar"
                      style={{ width: `${(sentRecords / totalRecords) * 100}%`, backgroundColor: t.accent }}
                    ></div>
                  </div>
                </div>

                <div className="row g-3 mb-4">
                  <div className="col-6">
                    <div className="rounded-3 p-3 text-center h-100 d-flex flex-column justify-content-center" style={{ backgroundColor: t.boxBg, border: `1px solid ${t.cardBorder}` }}>
                      <small className="d-block text-uppercase fw-bold mb-1" style={{ fontSize: '0.75rem', color: t.subheading }}>Remaining</small>
                      <span className="fs-3 fw-bolder" style={{ color: t.remainingText }}>{totalRecords - sentRecords}</span>
                    </div>
                  </div>
                  <div className="col-6">
                    <div className="rounded-3 p-3 text-center border h-100 d-flex flex-column justify-content-center" style={{ backgroundColor: t.statusBg, borderColor: t.statusBorder }}>
                      <small className="d-block text-uppercase fw-bold mb-1" style={{ fontSize: '0.75rem', color: t.statusLabel }}>Status</small>
                      <span className="fs-4 fw-bolder d-flex align-items-center justify-content-center gap-2" style={{ color: t.statusText }}>
                        <span className="spinner-grow spinner-grow-sm" style={{ color: t.statusSpinner }} role="status"></span>
                        ACTIVE
                      </span>
                    </div>
                  </div>
                </div>

                <div className="pt-4 text-center mt-auto" style={{ borderTop: `1px solid ${t.cardBorder}` }}>
                  <small className="fw-bold text-uppercase tracking-wider" style={{ fontSize: '0.75rem', color: t.subheading }}>Transmitting to</small>
                  <p className="mt-1 fs-4 fw-bold font-monospace mb-0 text-truncate w-100" key={`phone-${sentRecords}`} style={{ animation: 'contentSlideIn 0.3s ease-out', color: t.transmitText }}>
                    +{activeMessage?.phone || 'Loading...'}
                  </p>
                </div>

              </div>
            </div>

          </div>
        </div>
      )}

      {/* 3. SUCCESS / COMPLETED AREA */}
      {isCompleted && (
        <>
          <style>{`
            @keyframes successPop {
              0% { transform: scale(0.5); opacity: 0; }
              70% { transform: scale(1.1); opacity: 1; }
              100% { transform: scale(1); opacity: 1; }
            }
            @keyframes fadeInUpStagger {
              0% { opacity: 0; transform: translateY(15px); }
              100% { opacity: 1; transform: translateY(0); }
            }
            .animate-pop { animation: successPop 0.5s cubic-bezier(0.16, 1, 0.3, 1) forwards; }
            .animate-fade-up-1 { opacity: 0; animation: fadeInUpStagger 0.5s ease-out 0.15s forwards; }
            .animate-fade-up-2 { opacity: 0; animation: fadeInUpStagger 0.5s ease-out 0.25s forwards; }
            .animate-fade-up-3 { opacity: 0; animation: fadeInUpStagger 0.5s ease-out 0.35s forwards; }
          `}</style>

          <div className="card shadow-lg border-0 rounded-4 p-4 p-md-5 w-100 text-center bubble-entry-animate" style={{ maxWidth: '550px', backgroundColor: t.cardBg, border: `1px solid ${t.cardBorder} !important`, boxShadow: t.shadow, transition: 'all 0.3s ease' }}>

            <div className="d-flex justify-content-center mb-4">
              <div className="d-flex justify-content-center align-items-center rounded-circle animate-pop" style={{ width: '90px', height: '90px', backgroundColor: t.successIconBg }}>
                <svg width="46" height="46" viewBox="0 0 24 24" fill="none" stroke={t.successIconColor} strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="20 6 9 17 4 12"></polyline>
                </svg>
              </div>
            </div>

            <h2 className="fw-bolder mb-2 animate-fade-up-1" style={{ color: t.heading, fontSize: '2.2rem' }}>Broadcast Complete!</h2>
            <p className="mb-5 animate-fade-up-2" style={{ color: t.subheading, fontSize: '1.1rem' }}>
              Successfully processed and delivered <strong style={{ color: t.heading }}>{totalRecords}</strong> voter slips to the network.
            </p>

            <div className="d-flex flex-column gap-3 animate-fade-up-3">
              <button
                onClick={() => {
                  setIsCompleted(false);
                  setFile(null);
                  setHeaderImage(null);
                  setStatus('');
                  setTotalRecords(0);
                  setSentRecords(0);
                }}
                className="btn btn-primary btn-lg w-100 py-3 fw-bold rounded-3 shadow-sm d-flex justify-content-center align-items-center gap-2"
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21.5 2v6h-6M21.34 15.57a10 10 0 1 1-.92-10.26l1.08 1.19" />
                </svg>
                Start New Broadcast
              </button>
            </div>

          </div>
        </>
      )}

    </div>
  );
} 